import { startTransition, useCallback, useEffect, useState } from 'react';
import { useGetLastViewedPathQuery } from '@frameio/graphql-client/graphql/features/integrations';
import { getLinkForWorkspaces } from '@frameio/shared/routing/pages/workspaces';
import { isServer } from '@frameio/utils';

/**
 * An object containing the active tab index, navigation handlers, and loading state.
 */
export type UseIntegrationTabsResultType = {
  /** The index of the active tab (0 for 'Exports', 1 for 'Browse'). */
  activeTab: number;
  /** A flag indicating if the initial data is being fetched. */
  isLoading: boolean;
  /** Navigate to the 'Browse' tab. No-op if loading or already on browse. */
  handleBrowseTabSelection: () => void;
  /** Navigate to the 'Exports' tab. No-op if loading or already on exports. */
  handleExportTabSelection: () => void;
};

type UseIntegrationTabsProps = {
  /** The current URL path from `usePathname`. */
  currentPath: string;
  /** A callback function to handle navigation. */
  onNavigate: (path: string) => void;
};

const EXPORT_PATH = '/sequences';

/**
 * A list of paths that should be ignored when calculating the final path for the 'Browse' tab.
 */
const IGNORED_PATHS = [
  '/logout',
  '/authenticating',
  '/auth/callback',
  '/auth/login',
  '/unauthenticated',
  '/auth/refresh',
  EXPORT_PATH,
];

/**
 * A custom hook to manage the state and logic for integration tabs.
 *
 * This hook encapsulates the business logic for the tabbed navigation between the 'Exports' view
 * and the general 'Browse' view within an integration. It handles:
 * - Determining the currently active tab based on the URL path.
 * - Fetching the user's last viewed path to provide a seamless navigation experience.
 * - Calculating the correct destination URL for the 'Browse' tab.
 * - Providing memoized click handlers for tab navigation to prevent unnecessary re-renders.
 *
 * @param {UseIntegrationTabsProps} props - The props for the hook.
 * @returns {UseIntegrationTabsResultType}
 */
export const useIntegrationTabs = ({
  currentPath,
  onNavigate,
}: UseIntegrationTabsProps): UseIntegrationTabsResultType => {
  const [workspacesPath, setWorkspacesPath] = useState<string | null>(null);
  const activeTab = currentPath === EXPORT_PATH ? 0 : 1;

  const { data: lastViewedPathResult, loading } = useGetLastViewedPathQuery({
    context: {
      resolveWithUXP: true,
    },
    skip: isServer,
  });

  const lastViewedPath = lastViewedPathResult?.getLastViewedPath?.path;

  const finalPath = calculateBrowsePath(
    currentPath,
    lastViewedPath,
    loading,
    workspacesPath
  );

  const handleExportTabSelection = useCallback(() => {
    if (loading || activeTab === 0) {
      return;
    }

    startTransition(() => {
      onNavigate(EXPORT_PATH);
    });
  }, [loading, onNavigate, activeTab]);

  const handleBrowseTabSelection = useCallback(() => {
    if (loading || activeTab === 1) {
      return;
    }

    startTransition(() => {
      onNavigate(finalPath);
    });
  }, [finalPath, onNavigate, loading, activeTab]);

  useEffect(() => {
    if (!loading && lastViewedPath && !IGNORED_PATHS.includes(lastViewedPath)) {
      setWorkspacesPath(lastViewedPath);
    }
  }, [lastViewedPath, loading]);

  return {
    activeTab,
    isLoading: Boolean(loading),
    handleBrowseTabSelection,
    handleExportTabSelection,
  };
};

/**
 * Calculates the destination path for the 'Browse' tab.
 *
 * This function determines the correct URL to navigate to when the user clicks on the 'Browse' tab.
 * It ensures a seamless user experience by returning the user to their last viewed page.
 *
 * The logic is as follows:
 * 1. If data is still loading, it returns the current path to prevent premature navigation.
 * 2. If the user is not on the export path, it keeps them on the current path.
 * 3. If the user is on the export path, it attempts to navigate to their last viewed path.
 * 4. If a last viewed path isn't available, it falls back to a default workspaces URL.
 *
 * @returns {string} The calculated path for the 'Browse' tab.
 */
export function calculateBrowsePath(
  currentPath: string,
  lastViewedPath: string | null | undefined,
  isLoading: boolean,
  workspacesPath: string | null
): string {
  if (isLoading) {
    return currentPath;
  }

  if (currentPath !== EXPORT_PATH) {
    return currentPath;
  }

  if (lastViewedPath && lastViewedPath !== EXPORT_PATH) {
    return lastViewedPath;
  }

  return (
    workspacesPath ||
    getLinkForWorkspaces(
      {
        page: 'workspaces',
      },
      true
    )
  );
}
